import React, { useEffect, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";

import {
  sendForgotPasswordOtp,
  verifyForgotPasswordOtp,
} from "../Redux/ReduxSlices/forgetPasswordSlice";

// ✅ import your dialog components
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const { error, success } = useSelector((state) => state.forgetpassword);

  const [step, setStep] = useState(1);
  const [showNewPass, setShowNewPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const [formData, setFormData] = useState({
    email: "",
    otp: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => setResendTimer((prev) => prev - 1), 1000);
    }
    return () => interval && clearInterval(interval);
  }, [resendTimer]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateEmailStep = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Invalid email format";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePasswordStep = () => {
    const newErrors = {};
    if (!formData.otp) newErrors.otp = "OTP is required";
    else if (formData.otp.length !== 6) newErrors.otp = "OTP must be 6 digits";

    if (!formData.newPassword) newErrors.newPassword = "New password is required";
    else if (formData.newPassword.length < 6)
      newErrors.newPassword = "Password must be at least 6 characters";

    if (!formData.confirmPassword) newErrors.confirmPassword = "Please confirm password";
    else if (formData.newPassword !== formData.confirmPassword)
      newErrors.confirmPassword = "Passwords do not match";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmailStep()) return;

    setLoading(true);
    try {
      const action = await dispatch(sendForgotPasswordOtp(formData.email));
      const data = action?.payload;

      if (data?.success) {
        toast.success(data.message);
        setStep(2);
      } else {
        toast.error(data?.message || "Failed to send OTP");
      }
    } catch (err) {
      toast.error("Something went wrong while sending OTP");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!validatePasswordStep()) return;

    setLoading(true);
    try {
      const data = await dispatch(
        verifyForgotPasswordOtp({
          email: formData.email,
          otp: formData.otp,
          newPassword: formData.newPassword,
          confirmPassword: formData.confirmPassword,
        })
      ).unwrap();

      toast.success(data?.message || "Password reset successfully!");
      handleClose();
    } catch (err) {
      toast.error(err?.message || "Password reset failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!formData.email) return toast.error("Email not found");
    if (resendTimer > 0) return;

    try {
      setResendLoading(true);
      const response = await dispatch(sendForgotPasswordOtp(formData.email)).unwrap();
      toast.success(response?.message || "OTP resent successfully");
      setResendTimer(600);
    } catch (error) {
      toast.error(error || "Something went wrong");
    } finally {
      setResendLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setFormData({
      email: "",
      otp: "",
      newPassword: "",
      confirmPassword: "",
    });
    setErrors({});
    onClose?.();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        // ✅ When user clicks overlay / presses Esc => close
        if (!open) handleClose();
      }}
    >
      <DialogContent className="w-[95vw] max-w-md p-0 ">
        {/* Header */}
        <div className="flex justify-between items-center p-5 border-b border-black/20">
          <DialogHeader className="space-y-0 text-left">
            <DialogTitle className="text-xl font-semibold text-black">
              {step === 1 ? "Reset Password" : "Create New Password"}
            </DialogTitle>
          </DialogHeader>

     
        </div>

        {/* Steps */}
        <div className="flex justify-center py-4">
          <div className="flex items-center">
            <div
              className={`w-8 h-8 flex items-center justify-center rounded-full ${
                step >= 1 ? "main-button text-white" : "bg-gray-300 text-black"
              }`}
            >
              1
            </div>
            <div
              className={`w-12 h-[2px] mx-2 ${step >= 2 ? "bg-black" : "bg-gray-300"}`}
            />
            <div
              className={`w-8 h-8 flex items-center justify-center rounded-full ${
                step >= 2 ? "main-button text-white" : "bg-gray-300 text-black"
              }`}
            >
              2
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 1 ? (
            <form onSubmit={handleEmailSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-black mb-1">
                  Enter your email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md outline-none ${
                    errors.email ? "border-red-500" : "border-black/40"
                  }`}
                  placeholder="Enter your email"
                />
                {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 text-sm main-button text-white rounded-md hover:bg-gray-800 disabled:opacity-50"
                >
                  {loading ? "Sending..." : "Send OTP"}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handlePasswordSubmit}>
              <div className="space-y-4">
                {/* OTP */}
                <div className="relative">
                  <label className="block text-sm text-black mb-1">Enter OTP</label>
                  <input
                    maxLength={6}
                    type="text"
                    name="otp"
                    value={formData.otp}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md outline-none ${
                      errors.otp ? "border-red-500" : "border-black/40"
                    }`}
                    placeholder="Enter 6-digit OTP"
                  />
                  {errors.otp && <p className="text-red-500 text-sm">{errors.otp}</p>}

                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={resendLoading || resendTimer > 0}
                    className="absolute right-0 font-medium -bottom-6 text-xs text-blue-600 hover:underline disabled:text-gray-400"
                  >
                    {resendLoading
                      ? "Sending..."
                      : resendTimer > 0
                        ? `Resend in ${formatTime(resendTimer)}`
                        : "Resend OTP"}
                  </button>
                </div>

                {/* New Password */}
                <div>
                  <label className="block text-sm text-black mb-1 mt-6">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPass ? "text" : "password"}
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-md outline-none ${
                        errors.newPassword ? "border-red-500" : "border-black/40"
                      }`}
                      placeholder="Enter new password"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-2.5 text-black"
                      onClick={() => setShowNewPass((s) => !s)}
                    >
                      {showNewPass ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {errors.newPassword && (
                    <p className="text-red-500 text-sm">{errors.newPassword}</p>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm text-black mb-1">Confirm Password</label>
                  <div className="relative">
                    <input
                      type={showConfirmPass ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border rounded-md outline-none ${
                        errors.confirmPassword ? "border-red-500" : "border-black/40"
                      }`}
                      placeholder="Confirm password"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-2.5 text-black"
                      onClick={() => setShowConfirmPass((s) => !s)}
                    >
                      {showConfirmPass ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-red-500 text-sm">{errors.confirmPassword}</p>
                  )}
                </div>
              </div>

              <div className="flex justify-between mt-6">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="px-4 py-2 text-sm bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Back
                </button>

                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 text-sm main-button text-white rounded-md hover:bg-gray-800 disabled:opacity-50"
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </button>
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordModal;